import React, {
  useContext,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import { envelope, featureCollection } from "@turf/turf";
import { useMutation } from "@tanstack/react-query";
import { X, PaintBrushHousehold, Download, Info } from "phosphor-react";
import { isMobile } from "react-device-detect";
import { isEqual } from "lodash";
import { UserContext } from "context/user";
import { MapContext, MapActions } from "context/map";
import { updateZoneOrder } from "api/ZoneOrder";
import InputField from "components/Input";
import Button from "components/Button";
import "./index.scss";
import ReactTooltip from "react-tooltip";
import { updateStackOrder } from "api/StackOrder";

const zoneFillColors = {
  2: ["match", ["get", "zone"], 1, "#ffe75e", 2, "#34ffa1", "#FFFFFF"],
  3: [
    "match",
    ["get", "zone"],
    1,
    "#ff9966",
    2,
    "#b9ff55",
    3,
    "#1260ff",
    "#FFFFFF",
  ],
  4: [
    "match",
    ["get", "zone"],
    1,
    "#ff9966",
    2,
    "#fcff54",
    3,
    "#54f174",
    4,
    "#1260ff",
    "#FFFFFF",
  ],
  5: [
    "match",
    ["get", "zone"],
    1,
    "#ff9966",
    2,
    "#ffe75e",
    3,
    "#b9ff55",
    4,
    "#34ffa1",
    5,
    "#1260ff",
    "#FFFFFF",
  ],
};

const EditZoneFarm = () => {
  const { token } = useContext(UserContext);
  const { state, dispatch } = useContext(MapContext);
  const { map, zoneData, selectedFields, fields, selectedLayer } = state;
  const { zonedFields, zoningOrderId, numberOfZones, rates, features } =
    zoneData;
  const [zoneLoaded, setZoneLoaded] = useState(false);
  const [zoneRates, setZoneRates] = useState(rates);
  const [selectedZone, setSelectedZone] = useState(null);
  const [zoneChanged, setZoneChanged] = useState(false);
  const prevZoneRatesRef = useRef(zoneRates);
  const [updatedZoneData, setUpdatedZoneData] = useState(null);
  const updateZone = useMutation({
    mutationFn: (updatedZone) => {
      if (selectedLayer === "stack") {
        return updateStackOrder(
          { id: zoningOrderId, zones: updatedZone },
          token
        );
      }
      return updateZoneOrder({ id: zoningOrderId, zones: updatedZone }, token);
    },
    onSettled: (data) => {
      setUpdatedZoneData(data);
      setZoneChanged(false);
    },
  });

  useEffect(() => {
    if (selectedZone) {
      const zoneColors = {
        2: { 1: "ffe75e", 2: "34ffa1" },
        3: { 1: "ff9966", 2: "b9ff55", 3: "1260ff" },
        4: { 1: "ff9966", 2: "fcff54", 3: "54f174", 4: "1260ff" },
        5: {
          1: "ff9966",
          2: "ffe75e",
          3: "b9ff55",
          4: "34ffa1",
          5: "1260ff",
        },
      };

      const mapContainer = document.querySelector(".mapboxgl-canvas-container");

      mapContainer.classList.remove(
        "cursor-ff9966",
        "cursor-ffe75e",
        "cursor-b9ff55",
        "cursor-34ffa1",
        "cursor-1260ff",
        "cursor-fcff54",
        "cursor-54f174"
      );
      mapContainer.classList.add(
        `cursor-${zoneColors[numberOfZones][selectedZone]}`
      );
    }

    return () => {
      const mapContainer = document.querySelector(".mapboxgl-canvas-container");
      mapContainer?.classList.remove(
        "cursor-ff9966",
        "cursor-ffe75e",
        "cursor-b9ff55",
        "cursor-34ffa1",
        "cursor-1260ff",
        "cursor-fcff54",
        "cursor-54f174"
      );
    };
  });

  useEffect(() => {
    const zoneSource = map.getSource("zones");
    const zoneFill = map.getLayer("zones");
    const zoneOutline = map.getLayer("zones-outline");

    if (zoneSource) {
      zoneSource.setData({
        type: "FeatureCollection",
        features,
      });
    } else {
      map.addSource("zones", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features,
        },
      });
    }

    if (!zoneFill) {
      map.addLayer({
        id: "zones",
        type: "fill",
        source: "zones",
        paint: {
          "fill-opacity": 1,
        },
      });
    }

    if (!zoneOutline) {
      map.addLayer({
        id: "zones-outline",
        type: "line",
        source: "zones",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#6E6E6E",
          "line-width": 0.6,
        },
      });
    }

    map.setPaintProperty("zones", "fill-color", zoneFillColors[numberOfZones]);

    setZoneLoaded(true);
  }, [features, map, numberOfZones]);
  const zoomSelectedFields = useCallback(() => {
    if (!zoneLoaded) return;

    const padding = {
      left: -380,
      top: 100,
      right: 0,
      bottom: 150,
    };
    const fieldsFilter = selectedFields.length ? selectedFields : zonedFields;

    const filteredFields = fieldsFilter.map((id) =>
      fields.find((field) => field.id.toString() === id.toString())
    );
    const zonedFieldFeatures = featureCollection(filteredFields);
    map.fitBounds(envelope(zonedFieldFeatures).bbox, {
      padding,
    });
  }, [fields, map, selectedFields, zoneLoaded, zonedFields]);

  useEffect(() => {
    zoomSelectedFields();
  }, [zoomSelectedFields]);

  useEffect(() => {
    const handleClick = (e) => {
      if (!selectedZone) return;

      // eslint-disable-next-line no-underscore-dangle
      const sourceData = map.getSource("zones")._data;
      const clickedFeature = e.features[0];
      const featureToUpdate = sourceData.features.find((feature) => {
        return feature.id.toString() === clickedFeature.id.toString();
      });
      featureToUpdate.properties.zone = parseInt(selectedZone, 10);
      map.getSource("zones").setData(sourceData);
      setZoneChanged(true);
      setUpdatedZoneData(null);
    };

    if (isMobile) {
      map.off("touchstart", "zones", handleClick);
      map.on("touchstart", "zones", handleClick);
    } else {
      map.off("click", "zones", handleClick);
      map.on("click", "zones", handleClick);
    }

    return () => {
      if (isMobile) {
        map.off("touchstart", "zones", handleClick);
      } else {
        map.off("click", "zones", handleClick);
      }
    };
  }, [map, selectedZone]);

  useEffect(() => {
    const hasZoneChanged = !isEqual(zoneRates, prevZoneRatesRef.current);

    setZoneChanged(hasZoneChanged);
    setUpdatedZoneData(null);
    prevZoneRatesRef.current = zoneRates;
  }, [zoneRates]);

  const getZoneColor = (zone) => {
    const zoneColors = {
      2: { 1: "#ffe75e", 2: "#34ffa1" },
      3: { 1: "#ff9966", 2: "#b9ff55", 3: "#1260ff" },
      4: { 1: "#ff9966", 2: "#fcff54", 3: "#54f174", 4: "#1260ff" },
      5: {
        1: "#ff9966",
        2: "#ffe75e",
        3: "#b9ff55",
        4: "#34ffa1",
        5: "#1260ff",
      },
    };

    return zoneColors[numberOfZones][zone];
  };

  const getZoneTotalArea = useCallback(
    (zoneNumber) => {
      const zoneSource = map.getSource("zones");

      if (!zoneSource) return null;

      // eslint-disable-next-line no-underscore-dangle
      const sourceData = zoneSource._data;

      const zoneFeatures = sourceData.features.filter(
        (feature) =>
          feature.properties.zone.toString() === zoneNumber.toString()
      );
      const totalArea = zoneFeatures.reduce(
        (acc, feature) => acc + feature.properties.area,
        0
      );

      return totalArea.toFixed(1);
    },
    [map]
  );

  const getZoneRate = (zone) => {
    return zoneRates[zone];
  };

  const updateZoneRate = (zone, rate) => {
    if (rate === "" || /^\d+(\.\d*)?$/.test(rate)) {
      setZoneRates((prevRates) => ({ ...prevRates, [zone]: rate }));
    }
  };

  const removeZones = useCallback(() => {
    const zonesLayer = map.getLayer("zones");
    const zonesOutlineLayer = map.getLayer("zones-outline");
    const zoneSource = map.getSource("zones");

    if (zonesLayer) {
      map.removeLayer("zones");
    }

    if (zonesOutlineLayer) {
      map.removeLayer("zones-outline");
    }

    if (zoneSource) {
      map.removeSource("zones");
    }
  }, [map]);

  const handleGoBack = () => {
    removeZones();

    dispatch({
      type: MapActions.SET_VIEW_MODE,
    });
  };

  const convertZoneRateValue = (rate) => {
    return ["", null].includes(rate) ? null : parseFloat(rate);
  };

  const prepareZoneRate = useCallback(() => {
    return Object.fromEntries(
      Object.entries(zoneRates).map(([key, value]) => [
        key,
        convertZoneRateValue(value),
      ])
    );
  }, [zoneRates]);

  const handleUpdateZone = () => {
    const zoneSource = map.getSource("zones");
    // eslint-disable-next-line no-underscore-dangle
    const sourceData = zoneSource._data;
    const newZoneData = { ...sourceData, rates: prepareZoneRate() };

    updateZone.mutate(newZoneData);
  };

  const getZoneRateValue = useCallback((zoneRate) => {
    const rate = ["", null].includes(zoneRate) ? 0 : parseFloat(zoneRate);

    return rate;
  }, []);

  const calculateTotalProductNeeded = useCallback(() => {
    const zonePrices = Array.from(
      { length: numberOfZones },
      (_, i) => i + 1
    ).map((zone) => getZoneTotalArea(zone) * getZoneRateValue(zoneRates[zone]));
    const totalProductNeeded = zonePrices.reduce((acc, rate) => acc + rate, 0);

    return totalProductNeeded % 1 === 0
      ? totalProductNeeded
      : totalProductNeeded.toFixed(2);
  }, [getZoneRateValue, getZoneTotalArea, numberOfZones, zoneRates]);

  const renderDownloadOption = () => {
    return (
      <div className="download-zones">
        <div className="items">
          <a
            href={`${process.env.REACT_APP_DATA_FARMING_API_ORIGIN}/v2/${selectedLayer}/download/${updatedZoneData.download_key}?unified=false`}
            className="item"
          >
            <Download size={20} color="#6F7D68" className="icon" />
            Individual Fields ({updatedZoneData.fields.length}{" "}
            {updatedZoneData.fields.length > 1 ? "Files" : "File"})
          </a>
          <a
            href={`${process.env.REACT_APP_DATA_FARMING_API_ORIGIN}/v2/${selectedLayer}/download/${updatedZoneData.download_key}?unified=true`}
            className="item"
          >
            <Download size={20} color="#6F7D68" className="icon" />
            Entire Farm (1 File)
          </a>
        </div>
      </div>
    );
  };

  if (!zoneLoaded) return <div className="zoning-process" />;

  return (
    <div className="zoning-process">
      <ReactTooltip
        className="tooltip"
        place="right"
        type="dark"
        effect="float"
        multiline
      />
      <div className="zoning-header">
        <h1 className="title">Edit Rates</h1>
        <button
          className="close-button"
          type="button"
          onClick={() => handleGoBack()}
        >
          <X size={20} color="#292b33" weight="bold" />
        </button>
      </div>
      <div className="rates-container">
        <div className="rates-header">
          <p className="text">Zone</p>
          <p className="text -area">Area (Ha)</p>
          <p className="text -info">
            Rate
            <Info
              size={20}
              weight="fill"
              color="#8a8e99"
              data-tip="Ensure the product units
              you are using match the rate controller requirements.
              Spray units will likely use L/Ha or Gal/acre.
              Fertiliser, lime etc will likely use kg/Ha or lb/acre."
            />
          </p>
        </div>
        <div className="rates-wrapper">
          {Array.from({ length: numberOfZones }, (_, i) => i + 1).map(
            (zone) => (
              <div className="rate-item" key={zone}>
                <div className="name">
                  <div
                    className="zone-color"
                    style={{ backgroundColor: getZoneColor(zone) }}
                  />
                  Zone {zone}
                </div>
                <div className="area">{getZoneTotalArea(zone)}</div>
                <div className="actions">
                  <InputField
                    type="text"
                    value={getZoneRate(zone)}
                    onChange={(event) => {
                      updateZoneRate(zone, event.target.value);
                    }}
                  />

                  <button
                    type="button"
                    className={`${
                      selectedZone === zone
                        ? "paint-button -active"
                        : "paint-button"
                    }`}
                    onClick={() => setSelectedZone(zone)}
                  >
                    <PaintBrushHousehold size={20} />
                  </button>
                </div>
              </div>
            )
          )}
        </div>
        <div className="product-content">
          {updatedZoneData && renderDownloadOption()}
          <h1 className="needed">{calculateTotalProductNeeded()}</h1>
          <div className="label">Total product needed</div>
          <Button
            color="green"
            block
            label="Export Files"
            disabled={!zoneChanged || updateZone.isLoading}
            onClick={() => handleUpdateZone()}
          />
          <div className="notice">Shape files to be provided upon export</div>
        </div>
      </div>
    </div>
  );
};

export default EditZoneFarm;
